import { useMemo, useState } from "react";
import { Modal } from "../../../components/Modal";
import {
  createWorkSubcontractor,
  type WorkSubcontractorItem,
} from "../../../obras/obrasApi";
import type { Subcontractor } from "../../../subempreiteiros/subempreiteirosApi";
import type { Supplier } from "../../../fornecedores/fornecedoresApi";
import { EntidadeExecutanteField } from "./EntidadeExecutanteField";

interface NovaLinhaSubempreitadaModalProps {
  workId: number;
  subcontractors: Subcontractor[];
  suppliers: Supplier[];
  onClose: () => void;
  onCreated: (item: WorkSubcontractorItem) => void;
}

const inputClass =
  "w-full px-2 py-1 border border-border rounded bg-background text-foreground";

/**
 * Criar uma linha de subempreitada numa obra já existente.
 *
 * Vive num componente próprio porque é aberto de dois sítios — do separador Sub-empreiteiros e do
 * beco sem saída do separador Autos, onde não há linhas para medir. Uma só implementação: os dois
 * caminhos acabam no mesmo `createWorkSubcontractor`.
 */
export function NovaLinhaSubempreitadaModal({
  workId,
  subcontractors,
  suppliers,
  onClose,
  onCreated,
}: NovaLinhaSubempreitadaModalProps) {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [subcontractor, setSubcontractor] = useState("");
  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fornecedores e sub-empreiteiros na mesma lista: são tabelas distintas, mas quem executa a obra
  // está registado ora numa ora noutra, e este campo é texto livre.
  const entidades = useMemo(() => {
    const nomes = new Set<string>();
    [...subcontractors, ...suppliers].forEach((entidade) => {
      const nome = entidade.name?.trim();
      if (nome) nomes.add(nome);
    });
    return Array.from(nomes).sort((a, b) => a.localeCompare(b, "pt-PT"));
  }, [subcontractors, suppliers]);

  const valorNumerico = Number(value.replace(",", "."));
  const podeGravar =
    code.trim() !== "" &&
    description.trim() !== "" &&
    subcontractor.trim() !== "" &&
    value.trim() !== "" &&
    Number.isFinite(valorNumerico) &&
    valorNumerico >= 0;

  const handleCreate = async () => {
    if (!podeGravar) return;
    setIsSaving(true);
    setError(null);
    try {
      const criada = await createWorkSubcontractor(workId, {
        code: code.trim(),
        description: description.trim(),
        subcontractor: subcontractor.trim(),
        value: valorNumerico,
      });
      onCreated(criada);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível criar a linha de subempreitada.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Nova linha de subempreitada"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-border"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isSaving || !podeGravar}
            className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "A criar…" : "Criar linha"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Código *
          <input
            type="text"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className={inputClass}
            placeholder="Ex.: 4.1"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Descrição *
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className={inputClass}
            placeholder="Ex.: Canalização e esgotos"
          />
        </label>

        <div className="flex flex-col gap-1 text-sm text-secondary-text">
          Quem executa *
          <EntidadeExecutanteField
            value={subcontractor}
            onChange={setSubcontractor}
            options={entidades}
            inputClassName={inputClass}
          />
        </div>

        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Valor contratado (€) *
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className={inputClass}
            placeholder="Ex.: 10000"
          />
          {/* O auto mede o executado contra este valor. Criar a linha a zero fazia a comparação
              "medido vs contratado" passar a comparar contra nada. */}
          <span className="text-xs text-secondary-text">
            É contra este valor que os autos medem o que foi executado.
          </span>
        </label>
      </div>
    </Modal>
  );
}
